<template>
  <!-- «Cuándo: … ▾»: el disparador en una pastilla; el popover edita dto.trigger* directamente (los campos de
       texto no pasan por el historial). Ámbar si el disparador no es válido. -->
  <div ref="rootRef" class="tc" @keydown.esc="onEsc">
    <button ref="btnRef" type="button" class="tc-pill" :class="{ bad: !valid }" aria-haspopup="dialog" :aria-expanded="isOpen"
            @click="isOpen ? close(false) : open()">
      {{ t('when.chip', { v: summary }) }} <span aria-hidden="true">▾</span>
    </button>
    <Transition name="vem-pop">
      <div v-if="isOpen" class="tc-pop" role="dialog" :aria-label="t('ed.when')">
        <div ref="segRef" class="seg" role="radiogroup" :aria-label="t('ed.when')">
          <button v-for="tt in TRIGGERS" :key="tt" type="button" role="radio" :aria-checked="dto.triggerType === tt"
                  :class="{ on: dto.triggerType === tt }" @click="dto.triggerType = tt">{{ t('trigger.' + tt) }}</button>
        </div>
        <div class="tc-row">
          <template v-if="dto.triggerType === 'interval'">
            <span class="muted">{{ t('when.every') }}</span>
            <input v-model.number="dto.triggerEvery" type="number" min="1" class="in num" :aria-label="t('trigger.interval')" />
            <select v-model="dto.triggerUnit" class="in" :aria-label="t('trigger.interval')">
              <option v-for="u in UNITS" :key="u" :value="u">{{ t('unit.' + u) }}</option>
            </select>
          </template>
          <input v-else-if="dto.triggerType === 'cron'" v-model="dto.triggerCron" class="in mono tc-cron" :class="{ bad: !valid }"
                 placeholder="0 0 3 * * *" :aria-label="t('trigger.cron')" :aria-invalid="!valid" />
          <span v-else class="hint">{{ dto.triggerType === 'once' ? t('trigger.onceHint') : t('trigger.onlyManual') }}</span>
        </div>
        <p class="hint">{{ t('trigger.tickHint') }}</p>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';
import { cronValid } from '@vem/readiness.js';

const TRIGGERS = ['cron', 'interval', 'manual'];
const UNITS = ['m', 'h', 'd'];

const props = defineProps({
  /** dto del editor: se editan triggerType, triggerCron, triggerEvery y triggerUnit. */
  dto: { type: Object, required: true },
});

const isOpen = ref(false);
const rootRef = ref(null);
const btnRef = ref(null);
const segRef = ref(null);

const valid = computed(() => {
  const d = props.dto;
  if (d.triggerType === 'cron') return cronValid(d.triggerCron);
  if (d.triggerType === 'interval') return Number.isInteger(d.triggerEvery) && d.triggerEvery >= 1;
  return true;
});

const summary = computed(() => {
  const d = props.dto;
  if (d.triggerType === 'interval') return t('trigger.every', { n: d.triggerEvery, unit: t('unit.' + d.triggerUnit) });
  if (d.triggerType === 'cron') return t('when.cron', { v: d.triggerCron || '' });
  return t('when.' + (d.triggerType || 'manual'));
});

function open() {
  isOpen.value = true;
  nextTick(() => (segRef.value?.querySelector('button.on') || segRef.value?.querySelector('button'))?.focus());
}

function close(refocus) {
  isOpen.value = false;
  if (refocus) nextTick(() => btnRef.value?.focus());
}

function onEsc(e) {
  if (!isOpen.value) return;
  e.stopPropagation();
  close(true);
}

function onDocDown(e) {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(e.target)) close(false);
}

onMounted(() => document.addEventListener('pointerdown', onDocDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown));

defineExpose({ open });
</script>

<style>
.vem-root .tc { position: relative; }
.vem-root .tc-pill {
  display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: 4px 12px; border-radius: 100px;
  border: 1px solid var(--line2); background: var(--ink); color: var(--tx); cursor: pointer; font-weight: 600; font-size: 12.5px;
  white-space: nowrap;
}
.vem-root .tc-pill:hover { border-color: var(--tx3); }
.vem-root .tc-pill.bad { color: var(--amber); border-color: var(--amber-bd); background: var(--amber-bg, rgba(168, 100, 0, .08)); }
.vem-root .tc-pop {
  position: absolute; z-index: 20; top: calc(100% + 8px); left: 0; width: 360px; max-width: calc(100vw - 32px);
  background: var(--panel); border: 1px solid var(--line2); border-radius: 12px; padding: 12px; box-shadow: 0 14px 36px var(--shadow);
}
.vem-root .tc-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.vem-root .tc-row .hint { margin: 0; }
.vem-root .tc-cron { width: 100%; }
.vem-root .tc-cron.bad { border-color: var(--amber-bd); }
.vem-root .tc-pop > .hint { margin-top: 10px; }
</style>
